import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import classNames from 'classnames';

import BuildingLocationViewDeviceInfo from './device-info';
import Card from 'common/components/card';
import CardAttributes from 'common/components/card-attributes';
import CardContent from 'common/components/card-content';
import CardHeader from 'common/components/card-header';
import {t} from 'common/utils/translation-util';

import {useDevicesLayer} from './hooks';
import {
  useFloorImageLayer,
  useMap,
  useSavePosition,
} from 'common/hooks/map-hooks';

import 'mapbox-gl/dist/mapbox-gl.css';
import './index.scss';

mapboxgl.accessToken = window.settings.keys.mapbox;

mapboxgl.workerCount = 3;

const BuildingLocationViewCard = ({
  attributes,
  connectDragSource,
  customCardHeight,
  disableConfig,
  floor,
  geojsonUrl,
  lastLayoutUpdate,
  mapBearingAngle,
  mapCenter,
  mapPitchAngle,
  mapStyleLayer,
  mapZoom,
  report,
}) => {
  const mapRef = useRef(null);
  const mapId = `map-${report.id}-${lastLayoutUpdate}`;

  const [map, mapError, loaded] = useMap(
    mapRef,
    {
      bearing: mapBearingAngle,
      center: mapCenter,
      doubleClickZoom: false,
      pitch: mapPitchAngle,
      style: mapStyleLayer,
      zoom: mapZoom,
    },
    [report, lastLayoutUpdate],
  );

  const floorPlanError = useFloorImageLayer(map, floor, loaded, [
    floor,
    lastLayoutUpdate,
    loaded,
    map,
  ])[0];

  const [selectedDevice, setSelectedDevice] = useDevicesLayer(
    map,
    loaded,
    geojsonUrl,
    [geojsonUrl, lastLayoutUpdate, loaded, map],
  );

  useEffect(() => setSelectedDevice(), [map, loaded, geojsonUrl]);

  const [moved, onSavePosition] = useSavePosition(map, report.id, loaded);
  const error = floorPlanError || mapError;
  const hasDetails = attributes.length;

  return (
    <Card className={'BuildingLocationViewCard'}>
      {connectDragSource(
        <div>
          <CardHeader
            disableConfig={disableConfig}
            disableDownloadCsv={true}
            disableDownloadPointsCsv={true}
            disableSeparatorLine={true}
            priorityWarnings={report.priorityWarnings}
            reportId={report.id}
            title={report.name}
            warnings={report.warnings}
          />
        </div>,
      )}
      <div
        className={classNames('BuildingLocationViewCard--Map', {
          BottomRounded: !hasDetails,
        })}
        id={mapId}
        ref={mapRef}
        style={{height: customCardHeight}}>
        {error ? (
          <div className={'BuildingLocationViewCard--Error'}>{error}</div>
        ) : null}
        {moved ? (
          <a
            className={'MapCard-Map-save-state'}
            href={'#'}
            onClick={onSavePosition}>
            {t('Save map position')}
          </a>
        ) : null}
        {selectedDevice ? (
          <BuildingLocationViewDeviceInfo
            {...selectedDevice}
            onClose={() => setSelectedDevice()}
          />
        ) : null}
      </div>
      {hasDetails ? (
        <CardContent>
          <CardAttributes attributes={attributes} />
        </CardContent>
      ) : null}
    </Card>
  );
};

BuildingLocationViewCard.propTypes = {
  attributes: PropTypes.array,
  connectDragSource: PropTypes.func,
  customCardHeight: PropTypes.number,
  disableConfig: PropTypes.bool,
  floor: PropTypes.shape({
    id: PropTypes.number,
    buildingId: PropTypes.number,
    floorPlan: PropTypes.shape({
      tileUrl: PropTypes.string,
      type: PropTypes.shape({
        id: PropTypes.string,
        displayName: PropTypes.string,
      }),
    }),
  }),
  geojsonUrl: PropTypes.string.isRequired,
  lastLayoutUpdate: PropTypes.string.isRequired,
  mapBearingAngle: PropTypes.number,
  mapCenter: PropTypes.array.isRequired,
  mapPitchAngle: PropTypes.number,
  mapStyleLayer: PropTypes.string,
  mapZoom: PropTypes.number,
  report: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.shape({
      displayName: PropTypes.string,
    }),
    name: PropTypes.string,
    priorityWarnings: PropTypes.array,
    warnings: PropTypes.array,
  }).isRequired,
};

BuildingLocationViewCard.defaultProps = {
  attributes: [],
  connectDragSource: (x) => x,
  disableConfig: false,
  mapBearingAngle: 0,
  mapPitchAngle: 0,
  mapStyleLayer: 'mapbox://styles/tgeer/cka5muugr18wq1ip7xinov68e',
  mapZoom: 17,
};

export default BuildingLocationViewCard;
